// src/pages/stylist/PortfolioPage.tsx
import React, { useMemo, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import MediaViewer, { MediaViewerItem } from '../../components/common/MediaViewer';
import { useAppSelector } from '../../hooks/useRedux';
import { portfolioService } from '../../services/portfolioService';
import { useUI } from '../../context/UIContext';
import {
  ArrowBackIcon, AddPhotoIcon, DeleteIcon, EditIcon, CloseIcon
} from '../../components/common/Icons';
import '../../styles/PortfolioPage.css';

interface PortfolioImage {
  _id: string;
  url: string;
  caption?: string;
  category?: string;
  mediaType?: 'image' | 'video';
  createdAt?: string;
}

const PortfolioPage: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAppSelector(state => state.auth);
  const { setLoading: setUILoading, showSnackbar, showConfirmDialog } = useUI();

  const [portfolioImages, setPortfolioImages] = useState<PortfolioImage[]>([]);
  const [loading, setLoading] = useState(false);
  const [viewerVisible, setViewerVisible] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [videoViewerItems, setVideoViewerItems] = useState<MediaViewerItem[]>([]);
  const [editImage, setEditImage] = useState<{ image: PortfolioImage; visible: boolean } | null>(null);
  const [editCaption, setEditCaption] = useState('');

  useEffect(() => {
    fetchPortfolio();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  const fetchPortfolio = async () => {
    if (!user?.id) return;

    try {
      setLoading(true);
      const portfolioData = await portfolioService.getPortfolio(user.id);
      const flattenedImages = portfolioData.portfolio.reduce(
        (acc: PortfolioImage[], categoryObj) => [...acc, ...categoryObj.images],
        []
      );
      setPortfolioImages(flattenedImages);
    } catch (error) {
      console.error('Error fetching portfolio:', error);
      showSnackbar('Failed to load portfolio', 'error');
    } finally {
      setLoading(false);
    }
  };

  const imageItems = useMemo(
    () => portfolioImages.filter((item) => item.mediaType !== 'video'),
    [portfolioImages],
  );

  const imageViewerItems: MediaViewerItem[] = useMemo(
    () => imageItems.map((item) => ({ type: 'image' as const, src: item.url })),
    [imageItems],
  );

  const openImageViewer = (item: PortfolioImage) => {
    const index = imageItems.findIndex((img) => img._id === item._id);
    if (index >= 0) {
      setVideoViewerItems([]);
      setSelectedIndex(index);
      setViewerVisible(true);
    }
  };

  const handleThumbnailClick = (item: PortfolioImage) => {
    if (item.mediaType === 'video') {
      setVideoViewerItems([{ type: 'video', src: item.url }]);
      setSelectedIndex(0);
      setViewerVisible(true);
    } else {
      openImageViewer(item);
    }
  };

  const handleDelete = (imageId: string) => {
    showConfirmDialog(
      'Delete Image',
      'Are you sure you want to delete this image?',
      async () => {
        try {
          setUILoading(true);
          await portfolioService.deletePortfolioImage(imageId);
          await fetchPortfolio();
          showSnackbar('Image deleted successfully', 'success');
        } catch (error) {
          console.error('Delete error:', error);
          showSnackbar('Failed to delete image', 'error');
        } finally {
          setUILoading(false);
        }
      },
      undefined,
      {
        confirmText: 'Delete',
        cancelText: 'Cancel',
        severity: 'error',
      }
    );
  };

  const handleEdit = (item: PortfolioImage) => {
    setEditImage({ image: item, visible: true });
    setEditCaption(item.caption || '');
  };

  const handleSaveCaption = async () => {
    if (!editImage?.image) return;

    try {
      setUILoading(true);
      await portfolioService.updatePortfolioImage(editImage.image._id, {
        caption: editCaption,
        category: editImage.image.category,
      });
      await fetchPortfolio();
      showSnackbar('Image updated successfully', 'success');
      setEditImage(null);
      setEditCaption('');
    } catch (error: any) {
      console.error('Edit error:', error);
      showSnackbar(error.response?.data?.error || 'Failed to update image', 'error');
    } finally {
      setUILoading(false);
    }
  };

  return (
    <div className="portfolio-page">
      <div className="portfolio-header">
        <button className="portfolio-back" onClick={() => navigate(-1)}>
          <ArrowBackIcon />
        </button>
        <h1 className="portfolio-title">Manage Portfolio</h1>
        <button
          className="portfolio-add-btn"
          onClick={() => navigate('/stylist/portfolio/add')}
        >
          <AddPhotoIcon />
          Add Items
        </button>
      </div>

      <div className="portfolio-content">
        {loading ? (
          <div className="portfolio-loading">
            <div className="tesla-loader"></div>
          </div>
        ) : portfolioImages.length === 0 ? (
          <div className="portfolio-empty">
            <div className="portfolio-empty__icon">
              <AddPhotoIcon size={64} />
            </div>
            <h3 className="portfolio-empty__title">No Portfolio Items Yet</h3>
            <p className="portfolio-empty__text">
              Showcase your work by adding photos to your portfolio.
            </p>
            <button
              className="tesla-button tesla-button--primary"
              onClick={() => navigate('/stylist/portfolio/add')}
            >
              <AddPhotoIcon />
              Add Portfolio Items
            </button>
          </div>
        ) : (
          <div className="portfolio-grid">
            {portfolioImages.map((item) => (
              <div key={item._id} className="portfolio-item">
                <div
                  className={`portfolio-item__image${item.mediaType === 'video' ? '' : ' portfolio-item__image--clickable'}`}
                  onClick={() => handleThumbnailClick(item)}
                  onKeyDown={
                    item.mediaType === 'video'
                      ? undefined
                      : (e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            handleThumbnailClick(item);
                          }
                        }
                  }
                  role={item.mediaType === 'video' ? undefined : 'button'}
                  tabIndex={item.mediaType === 'video' ? undefined : 0}
                  aria-label={
                    item.mediaType === 'video'
                      ? undefined
                      : `View portfolio image${item.caption ? `: ${item.caption}` : ''}`
                  }
                >
                  {item.mediaType === 'video' ? (
                    <video src={item.url} className="portfolio-media" />
                  ) : (
                    <img src={item.url} alt={item.caption || 'Portfolio item'} className="portfolio-media" />
                  )}
                  <div className="portfolio-item__overlay">
                    {item.caption && (
                      <div className="portfolio-item__caption">{item.caption}</div>
                    )}
                    {item.category && (
                      <div className="portfolio-item__category">{item.category}</div>
                    )}
                  </div>
                </div>
                <div className="portfolio-item__actions">
                  <button
                    className="portfolio-item__action-btn"
                    onClick={() => handleEdit(item)}
                    title="Edit caption"
                  >
                    <EditIcon />
                  </button>
                  <button
                    className="portfolio-item__action-btn portfolio-item__action-btn--danger"
                    onClick={() => handleDelete(item._id)}
                    title="Delete"
                  >
                    <DeleteIcon />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <MediaViewer
        items={videoViewerItems.length > 0 ? videoViewerItems : imageViewerItems}
        open={viewerVisible}
        index={selectedIndex}
        onClose={() => {
          setViewerVisible(false);
          setVideoViewerItems([]);
        }}
      />

      {/* Edit Caption Modal */}
      {editImage && editImage.visible && (
        <div className="portfolio-edit-modal" onClick={() => setEditImage(null)}>
          <div className="portfolio-edit-content" onClick={(e) => e.stopPropagation()}>
            <div className="portfolio-edit-header">
              <h2 className="portfolio-edit-title">Edit Caption</h2>
              <button
                className="portfolio-edit-close"
                onClick={() => setEditImage(null)}
              >
                <CloseIcon />
              </button>
            </div>
            <div className="portfolio-edit-body">
              <div className="form-group">
                <label htmlFor="caption">Caption</label>
                <textarea
                  id="caption"
                  className="tesla-textarea"
                  value={editCaption}
                  onChange={(e) => setEditCaption(e.target.value)}
                  placeholder="Enter caption for this image"
                  rows={4}
                />
              </div>
            </div>
            <div className="portfolio-edit-actions">
              <button
                className="tesla-button tesla-button--outline"
                onClick={() => setEditImage(null)}
              >
                Cancel
              </button>
              <button
                className="tesla-button tesla-button--primary"
                onClick={handleSaveCaption}
              >
                Save
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PortfolioPage;
